/**
 * Normalisation de chaînes pour la recherche multilingue FR/AR/EN.
 *
 * Couvre :
 * - Latin : lowercase + NFD + strip diacritiques (̀-ͯ) + ponctuation
 * - Arabe : strip tashkīl (ً-ْ, ٰ) + tatweel (ـ)
 *           + variantes alif (أ|إ|آ|ٱ → ا) + ya/alif maksura (ى → ي)
 *           + ta marbouta (ة → ه) + hamza isolée
 *
 * Utilisé par la SearchBar (client) et la page résultats (server) pour que
 * « طبيب عام » trouve la spécialité « Médecin généraliste » et que
 * « الدار البيضاء » trouve la ville « Casablanca ».
 */

/** Normalise les marques arabes pour matching tolérant. */
export function normalizeArabic(s: string): string {
  return (
    s
      // Hamza portée par ya / waw → lettre porteuse. Sans ça, la décomposition
      // NFD de normalizeQuery coupait le mot en deux (« نسائية » → « نساي يه »,
      // « عائشة » → « عاي شه ») : le hamza combinant n'est pas une lettre.
      .replace(/ئ/g, "ي")
      .replace(/ؤ/g, "و")
      // Tashkīl (diacritiques arabes : fatha, kasra, damma, shadda, sukūn…)
      // Inclut alif khanjariyya (ٰ) + tatweel kashida (ـ)
      .replace(/[ً-ْٰـ]/g, "")
      // Alif variants → ا
      .replace(/[آأإٱ]/g, "ا")
      // Ya / alif maksura → ي
      .replace(/ى/g, "ي")
      // Ta marbouta ة → ه (souvent confondues à l'oral)
      .replace(/ة/g, "ه")
      // Hamza isolée ء → vide (souvent omise dans les requêtes)
      .replace(/ء/g, "")
  );
}

/**
 * Normalisation universelle : applique les règles latines ET arabes,
 * supprime la ponctuation, écrase les espaces multiples.
 *
 * `normalizeQuery("Médecin généraliste")` → `"medecin generaliste"`
 * `normalizeQuery("طَبِيبٌ عَامٌّ")`        → `"طبيب عام"`
 * `normalizeQuery("الدار البيضاء")`         → `"الدار البيضاء"` (déjà normalisé)
 * `normalizeQuery("أحمد")`                  → `"احمد"`
 */
/**
 * Retire les préfixes parasites issus du scrap cromc.ma (artefact observé sur
 * ~13 events GA4 / 30j : queries du type `« ville: casablanca »`,
 * `« spécialité: dermatologue »`). L'ancre `^` empêche de casser les usages
 * légitimes contenant ces mots ailleurs dans la query (ex. « psychologue de
 * ville », « spécialité reconnue »).
 */
function stripScrapArtifacts(s: string): string {
  return s
    .replace(/^\s*vil(le)?\s*[:：]\s*/i, "")
    .replace(/^\s*spe(cialit(e|y))?\s*[:：]\s*/i, "");
}

export function normalizeQuery(s: string): string {
  if (!s) return "";
  const stripped = stripScrapArtifacts(s);
  return normalizeArabic(stripped)
    .toLowerCase()
    .normalize("NFD")
    .replace(/[̀-ͯ]/g, "")
    .replace(/[^\p{L}\p{N} ]/gu, " ")
    // Mots latins et arabes collés (« dentisteقرب ») : deux mots.
    .replace(/(\p{Script=Latin})(?=\p{Script=Arabic})/gu, "$1 ")
    .replace(/(\p{Script=Arabic})(?=\p{Script=Latin})/gu, "$1 ")
    // Titre collé au nom (« الدكتورشقراوي ») : deux mots. « دكتوره » (féminin)
    // reste entier — au moins 3 lettres doivent suivre le titre.
    .replace(/(^|\s)((?:ال)?دكتور)(?=[\u0621-\u064A]{3,})/gu, "$1$2 ")
    .replace(/\s+/g, " ")
    .trim();
}

/**
 * Stopwords médicaux à retirer de la query AVANT matching.
 *
 * Pourquoi : `« dr lakhdar nawal »` ne matche pas `« Nawal Lakhdar »` car
 * le préfixe « dr » n'apparaît jamais dans firstName/lastName. Idem pour
 * `« rdv avec ... »`, `« trouver ... »`, `« كتاب ... »` (verbe arabe).
 *
 * On retire UNIQUEMENT en début de query ou comme token isolé — on ne touche
 * pas à l'intérieur d'un mot composé. La query nettoyée est ensuite passée
 * au matching (token-AND).
 */
const MEDICAL_STOPWORDS = new Set([
  // Titres médicaux FR/EN
  "dr", "docteur", "dre", "drs", "pr", "professeur", "professor", "doctor",
  // Intentions de recherche FR
  "rdv", "rendezvous", "rendez", "vous", "trouver", "chercher", "consulter",
  "voir", "consultation", "avec", "chez", "le", "la", "les", "un", "une",
  "des", "de", "du", "et", "ou", "pour", "a",
  // EN
  "find", "search", "the", "an", "and", "or", "for", "with",
  // AR (mots-outils fréquents)
  "في", "مع", "عند", "ال", "و", "أو", "من", "إلى", "علي", "على",
  // Titres AR (équivalents "dr/docteur") — strip absolu sûr : jamais présents
  // dans un nom de spé du catalogue (qui utilise « طبيب »). ⚠ « طبيب » N'EST PAS
  // un stopword (il fait partie de « طبيب عام » = médecin généraliste).
  "دكتور", "دكتوره", "الدكتور", "الدكتوره",
  // Ce que l'on cherche SUR la fiche, pas le nom : « docteur alain guidon
  // horaires », « عنوان دكتور يوسف دراز » (recherches sans résultat 10/2026).
  "horaire", "horaires", "adresse", "telephone", "tel", "numero", "avis",
  "localisation", "عنوان", "العنوان", "رقم", "هاتف", "الهاتف",
  // Catégories génériques qui pollueraient ("docteur de", "médecin de")
  "medecin", // ⚠️ uniquement comme stopword en présence d'autres mots — géré dans stripStopwords
]);

/**
 * Variante de MEDICAL_STOPWORDS qu'on ne retire QUE si la query a >=2 tokens
 * restants. Évite de transformer la query "médecin" en "" (vide), qui matcherait
 * tout.
 */
const MEDICAL_STOPWORDS_CONDITIONAL = new Set([
  "medecin", "medical", "clinique", "cabinet", "centre",
  // « spécialiste (de) » AR : conditionnel pour laisser « اخصائي القلب » →
  // « القلب » matcher cardiologue, sans casser une recherche « اخصائي » seule.
  "اخصائي", "اخصائيه", "اخصاي", "اخصائيين", "اخصائية",
]);

// Les requêtes passent par normalizeQuery (« أخصائي » → « اخصايي », « إلى » →
// « الي ») : les mots-outils doivent être comparés sous la même forme, sinon
// ceux qui portent une hamza ne sont jamais retirés.
for (const set of [MEDICAL_STOPWORDS, MEDICAL_STOPWORDS_CONDITIONAL]) {
  for (const w of [...set]) set.add(normalizeQuery(w));
}

/**
 * Strip préfixes médicaux + stopwords d'intention sur une query NORMALISÉE.
 *
 * Conserve l'ordre des tokens restants. Si tous les tokens sont stripped,
 * retourne la query originale (évite la query vide).
 *
 * `stripStopwords("dr hajar elboukhari")` → `"hajar elboukhari"`
 * `stripStopwords("rdv avec ophtalmologue")` → `"ophtalmologue"`
 * `stripStopwords("docteur")` → `"docteur"` (singleton préservé)
 */
export function stripStopwords(qNormalized: string): string {
  if (!qNormalized) return qNormalized;
  const tokens = qNormalized.split(" ").filter(Boolean);
  if (tokens.length === 0) return qNormalized;

  // Première passe : strip stopwords absolus
  const kept = tokens.filter((t) => !MEDICAL_STOPWORDS.has(t));

  // Si singleton (l'utilisateur a vraiment cherché juste "docteur"), garde tout
  if (kept.length === 0) return qNormalized;

  // Deuxième passe : strip stopwords conditionnels uniquement si >=2 tokens
  if (kept.length >= 2) {
    const final = kept.filter((t) => !MEDICAL_STOPWORDS_CONDITIONAL.has(t));
    if (final.length >= 1) return final.join(" ");
  }
  return kept.join(" ");
}

/**
 * Tokenize une query normalisée en tokens "utiles" (>=2 chars).
 *
 * Garantit un comportement déterministe pour le matching token-AND : on filtre
 * les tokens trop courts (1 char) qui matcheraient n'importe quoi via includes().
 *
 * `tokenize("hajar elboukhari")` → `["hajar", "elboukhari"]`
 * `tokenize("a b")` → `[]` (trop courts, normal pour un single-char input)
 */
export function tokenize(qNormalized: string): string[] {
  if (!qNormalized) return [];
  return qNormalized.split(" ").filter((t) => t.length >= 2);
}

/**
 * Pipeline complet : normalise + strip stopwords + tokenize.
 *
 * Utilitaire d'entrée pour tous les matchers (médecins, médicaments, cliniques).
 *
 * `prepareQuery("Dr. Hajar Elboukhari")` → `{ q: "hajar elboukhari", tokens: ["hajar", "elboukhari"] }`
 */
export function prepareQuery(s: string): { q: string; tokens: string[]; raw: string } {
  const raw = normalizeQuery(s);
  const q = stripStopwords(raw);
  return { q, tokens: tokenize(q), raw };
}

/**
 * Détecte si une chaîne contient des caractères du bloc arabe.
 * Permet de router le matching/scoring différemment selon la langue détectée.
 */
export function isArabicQuery(s: string): boolean {
  return /[؀-ۿ]/.test(s);
}

/**
 * Construit le mapping de synonymes Meilisearch à partir d'items multilingues.
 *
 * Pour chaque item (specialty, ville…), génère les associations bidirectionnelles
 * entre les noms FR / AR / EN / slug afin que Meilisearch matche peu importe la
 * langue tapée :
 *
 *   { "طبيب عام": ["médecin généraliste", "general practitioner", "generaliste"],
 *     "médecin généraliste": ["طبيب عام", "general practitioner", "generaliste"],
 *     ... }
 *
 * Les noms sont normalisés (lowercase, NFD, marques arabes) avant indexation
 * pour rester alignés avec la query passée par l'utilisateur.
 */
export function buildSearchSynonyms(
  items: Array<{
    nameFr?: string | null;
    nameAr?: string | null;
    nameEn?: string | null;
    slug?: string | null;
  }>,
): Record<string, string[]> {
  const synonyms: Record<string, string[]> = {};
  for (const item of items) {
    const variants = [item.nameFr, item.nameAr, item.nameEn, item.slug]
      .filter((v): v is string => Boolean(v))
      .map((v) => v.toLowerCase().trim())
      .filter(Boolean);
    if (variants.length < 2) continue;

    const unique = Array.from(new Set(variants));
    for (const key of unique) {
      const others = unique.filter((v) => v !== key);
      if (others.length === 0) continue;
      synonyms[key] = synonyms[key]
        ? Array.from(new Set([...synonyms[key], ...others]))
        : others;
    }
  }
  return synonyms;
}

/**
 * Retourne tous les noms localisés normalisés d'un item multilingue
 * (utilisé pour construire un haystack cross-langue).
 *
 * Exemple : `multilingualHaystack({ nameFr: "Cardiologue", nameAr: "طبيب القلب", nameEn: "Cardiologist" })`
 *  → `["cardiologue", "طبيب القلب", "cardiologist"]` (chacun normalisé).
 */
export function multilingualHaystack(item: {
  nameFr?: string | null;
  nameAr?: string | null;
  nameEn?: string | null;
  slug?: string | null;
}): string[] {
  const out: string[] = [];
  for (const v of [item.nameFr, item.nameAr, item.nameEn, item.slug]) {
    if (v) {
      const n = normalizeQuery(v);
      if (n) out.push(n);
    }
  }
  return out;
}
